import { SelectItemProps } from '@ui/src';
import { COFAndIndividualQuestionnarieFormValues } from '../../../individual/types';
import {
  AffilatedCompanyFormValues,
  ApplicationAgreement,
  ApplicationDocument,
  CRSAndFatcaFormValues,
  FormValues,
  NewApplicationIndividualStepsStatuses,
  NewApplicationStepStatus,
  NewApplicationSumSubStatus,
} from '../../types';
import {
  OngoingMonitring,
  OngoingStatus,
  ApplicationScheme,
  OngoingCheckingStatus,
} from '../../../common/types';

export const getStepOneStatus = (
  values: Partial<FormValues>,
  stepsStatus?: NewApplicationStepStatus,
  taxRequired?: boolean,
  clientsActivities?: SelectItemProps[],
  applicationScheme?: ApplicationScheme,
  questionnarieFields?: COFAndIndividualQuestionnarieFormValues,
  affilatedCompaniesValues?: AffilatedCompanyFormValues[],
) => {
  let stepOneStatus = NewApplicationStepStatus.AWAITING || stepsStatus;

  const taxCompleted = !taxRequired || (taxRequired && values.taxNumber);

  const employmentClientActivity = clientsActivities?.find(
    (item) => item.name === 'Employment',
  );

  const otherClientActivity = clientsActivities?.find(
    (item) => item.name === 'Other',
  );

  const selfEmployedActivity = clientsActivities?.find(
    (item) => item.name === 'Self-employed',
  );

  const businessOwnerActivity = clientsActivities?.find(
    (item) => item.name === 'Business Owner',
  );

  const stepOne: Partial<FormValues> = {
    firstName: values.firstName,
    lastName: values.lastName,
    phone: values.phone,
    dob: values.dob,
    email: values.email,
    isUSResident: values.isUSResident,
    participatesInPolitics: values.participatesInPolitics,
  };

  let generalInfoComplete = false;
  let activitiesComplete = true;
  let groupInformationComplete = true;
  let sourceOfIncomeComplete = true;
  let sourcesAndSizesOfWealthComplete = true;
  let netWorthComplete = true;
  let affilatedCompaniesComplete = true;

  if (applicationScheme?.id === 'a87db4b4-3c78-4564-bba1-c16a19553159') {
    groupInformationComplete = false;
    sourceOfIncomeComplete = false;
    sourcesAndSizesOfWealthComplete = false;
    netWorthComplete = false;
    activitiesComplete = false;
    affilatedCompaniesComplete = false;

    if (
      values.firstName &&
      values.lastName &&
      values.phone &&
      values.dob &&
      values.placeOfBirth &&
      values.birthCountryId &&
      questionnarieFields?.maritalStatus
    ) {
      generalInfoComplete = true;
    }

    if (questionnarieFields?.maritalStatus === 'MARRIED') {
      if (!questionnarieFields?.spouseFullName) {
        generalInfoComplete = false;
      } else {
        generalInfoComplete = true;
      }
    }

    if (
      questionnarieFields?.belongsToGroup?.toString() !== 'null' &&
      questionnarieFields?.belongsToGroup?.toString() !== 'undefined'
    ) {
      groupInformationComplete = true;
    }

    if (
      questionnarieFields?.requiresSourcesOfIncomes?.toString() !== 'null' &&
      questionnarieFields?.requiresSourcesOfIncomes?.toString() !== 'undefined'
    ) {
      if (questionnarieFields?.requiresSourcesOfIncomes === 'no') {
        sourceOfIncomeComplete = true;
      } else {
        sourceOfIncomeComplete = Boolean(
          questionnarieFields?.sourcesOfIncomes?.filter((item) => item.sourceId)
            ?.length,
        );
      }
    }

    if (
      questionnarieFields?.requiresSourceAndSizeOfWealth?.toString() !==
        'null' &&
      questionnarieFields?.requiresSourceAndSizeOfWealth?.toString() !==
        'undefined'
    ) {
      if (questionnarieFields?.requiresSourceAndSizeOfWealth === 'no') {
        sourcesAndSizesOfWealthComplete = true;
      } else {
        sourcesAndSizesOfWealthComplete = Boolean(
          questionnarieFields?.sourcesAndSizesOfWealth?.filter(
            (item) => item.sourceId,
          )?.length,
        );
      }
    }

    if (
      questionnarieFields?.requiresNetWorth?.toString() !== 'null' &&
      questionnarieFields?.requiresNetWorth?.toString() !== 'undefined'
    ) {
      if (questionnarieFields?.requiresNetWorth === 'no') {
        netWorthComplete = true;
      } else {
        netWorthComplete = Boolean(questionnarieFields?.netWorthAmount);
      }
    }

    if (values?.clientActivityId) {
      activitiesComplete = true;
    }

    if (values.clientActivityId === otherClientActivity?.value) {
      activitiesComplete = Boolean(
        activitiesComplete && values.typeOfActivityDescription,
      );
      stepOne.typeOfActivityDescription = values.typeOfActivityDescription;
    }

    if (values.clientActivityId === employmentClientActivity?.value) {
      activitiesComplete = Boolean(
        activitiesComplete &&
        values.employeePosition &&
        values.nameOfEmployer &&
        values.countryOfEmployerId &&
        questionnarieFields?.natureOfBusinessActivity,
      );

      stepOne.employeePosition = values.employeePosition;
      stepOne.nameOfEmployer = values.nameOfEmployer;
      stepOne.countryOfEmployerId = values.countryOfEmployerId;
    }

    if (values.clientActivityId === selfEmployedActivity?.value) {
      activitiesComplete = Boolean(
        activitiesComplete && questionnarieFields?.natureOfBusinessActivity,
      );
    }

    if (values.clientActivityId === businessOwnerActivity?.value) {
      activitiesComplete = Boolean(
        activitiesComplete &&
        questionnarieFields?.natureOfBusinessActivity &&
        questionnarieFields?.nameOfCompany &&
        questionnarieFields?.ownership &&
        values?.countryOfEmployerId,
      );

      stepOne.countryOfEmployerId = values.countryOfEmployerId;
    }

    if (
      questionnarieFields?.hasAffiliatedCompanies === 'no' ||
      (questionnarieFields?.hasAffiliatedCompanies === 'yes' &&
        affilatedCompaniesValues?.filter(
          (item) =>
            item.name &&
            item.registrationNumber &&
            item.mainActivities &&
            item.position &&
            item.countryId &&
            item.percentageOfOwnership,
        )?.length)
    ) {
      affilatedCompaniesComplete = true;
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (values.firstName && values.lastName && values.phone && values.dob) {
      generalInfoComplete = true;
    }
  }

  const stepOneCompleted = Boolean(
    activitiesComplete &&
    generalInfoComplete &&
    groupInformationComplete &&
    sourceOfIncomeComplete &&
    sourcesAndSizesOfWealthComplete &&
    netWorthComplete &&
    affilatedCompaniesComplete &&
    taxCompleted,
  );

  if (values.taxNumber) {
    stepOne.taxNumber = values.taxNumber;
  }

  if (stepOneCompleted) {
    stepOneStatus = NewApplicationStepStatus.COMPLETED;
  } else if (
    Object.keys(stepOne).filter((key) => stepOne[key as keyof FormValues])
      .length
  ) {
    stepOneStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  return stepOneStatus;
};

export const getStepTwoStatus = (
  values: Partial<FormValues>,
  stepsStatus?: NewApplicationStepStatus,
  kycProvider?: string | null,
  kycRequired?: boolean | null,
  applicationScheme?: ApplicationScheme,
  questionnarieFields?: COFAndIndividualQuestionnarieFormValues,
  crsAndFatcaValues?: CRSAndFatcaFormValues[],
  haveSecondPassport?: boolean,
  documents?: ApplicationDocument[],
) => {
  let stepTwoStatus = NewApplicationStepStatus.AWAITING || stepsStatus;

  let stepTwoCompleted = false;
  let correspondenceAddressComplete = true;
  let secondPassportComplete = true;
  let pepComplete = true;
  let crsAndFatcaComplete = true;
  let documentsComplete = true;

  if (kycProvider === 'NONE') {
    stepTwoCompleted = true;
  } else if (kycProvider && !kycRequired) {
    stepTwoCompleted = true;
  } else if (kycProvider === null || (kycProvider && kycRequired)) {
    stepTwoCompleted = Boolean(
      values.sumSubStatus === NewApplicationSumSubStatus.GREEN,
    );
  }

  const stepTwo: Partial<
    FormValues &
      COFAndIndividualQuestionnarieFormValues & { haveSecondPassport: boolean }
  > = {
    countryId: values.countryId,
    city: values.city,
    postalCode: values.postalCode,
    address1: values.address1,
    address2: values.address2,
    sumSubStatus: values.sumSubStatus,
  };

  if (
    applicationScheme &&
    applicationScheme?.id === 'a87db4b4-3c78-4564-bba1-c16a19553159'
  ) {
    documentsComplete = false;

    if (
      documents?.filter(
        (item) => !item.documentCategoryId?.includes('identification'),
      )?.length
    ) {
      if (
        !documents
          ?.filter(
            (item) => !item.documentCategoryId?.includes('identification'),
          )
          ?.filter((item) => item.status === 'NEW').length
      ) {
        documentsComplete = true;
      }
    }
  }

  if (
    applicationScheme &&
    applicationScheme?.id === 'a87db4b4-3c78-4564-bba1-c16a19553159'
  ) {
    stepTwo.correspondenceAddressSameAsRegisteredAddress =
      values.correspondenceAddressSameAsRegisteredAddress;

    stepTwo.haveSecondPassport = haveSecondPassport;

    pepComplete = false;
    crsAndFatcaComplete = false;
    documentsComplete = false;

    if (values?.correspondenceAddressSameAsRegisteredAddress) {
      correspondenceAddressComplete = true;
    } else {
      correspondenceAddressComplete = Boolean(
        values.correspondenceAddress1 &&
        values.correspondenceAddress2 &&
        values.correspondenceCity &&
        values.correspondenceCountryId &&
        values.correspondencePostalCode,
      );

      stepTwo.correspondenceAddress1 = values.correspondenceAddress1;
      stepTwo.correspondenceAddress2 = values.correspondenceAddress2;
      stepTwo.correspondenceCity = values.correspondenceCity;
      stepTwo.correspondenceCountryId = values.correspondenceCountryId;
      stepTwo.correspondencePostalCode = values.correspondencePostalCode;
    }

    if (!haveSecondPassport) {
      secondPassportComplete = true;
    } else {
      secondPassportComplete = Boolean(
        questionnarieFields?.secondPassportExpiryDate &&
        questionnarieFields?.secondPassportIssueCountryId &&
        questionnarieFields?.secondPassportIssueDate &&
        questionnarieFields?.secondPassportNumber,
      );

      stepTwo.correspondenceAddress1 =
        questionnarieFields?.secondPassportExpiryDate;
      stepTwo.correspondenceAddress2 =
        questionnarieFields?.secondPassportIssueCountryId;
      stepTwo.correspondenceCity = questionnarieFields?.secondPassportIssueDate;
      stepTwo.correspondenceCountryId =
        questionnarieFields?.secondPassportNumber;
    }

    if (
      questionnarieFields?.isPepOrAssociate?.toString() !== 'null' &&
      questionnarieFields?.isPepOrAssociate?.toString() !== 'undefined'
    ) {
      if (questionnarieFields?.isPepOrAssociate === 'no') {
        pepComplete = true;
      } else {
        pepComplete = Boolean(
          questionnarieFields?.nameOfPep &&
          questionnarieFields?.relToPep &&
          questionnarieFields?.orgEmployerAndPosition &&
          questionnarieFields?.positionHeldCountryId &&
          questionnarieFields?.politicalFunctionPeriodFrom &&
          questionnarieFields?.politicalFunctionPeriodTo &&
          questionnarieFields?.abilityMoveGovernmentFunds,
        );

        if (
          questionnarieFields?.abilityMoveGovernmentFunds?.toString() !==
            'null' &&
          questionnarieFields?.abilityMoveGovernmentFunds?.toString() !==
            'undefined' &&
          questionnarieFields?.abilityMoveGovernmentFunds === 'yes'
        ) {
          if (!questionnarieFields?.abilityMoveGovernmentFundsDetails) {
            pepComplete = false;
          } else {
            pepComplete = Boolean(
              pepComplete &&
              questionnarieFields?.abilityMoveGovernmentFundsDetails,
            );
          }
        }
      }
    }

    if (
      crsAndFatcaValues?.filter(
        (item) =>
          item?.residenceCountryId &&
          (item?.taxIdentificationNumber || item?.noTinReason),
      )?.length
    ) {
      crsAndFatcaComplete = true;
    }

    if (
      documents?.filter(
        (item) => !item.documentCategoryId?.includes('identification'),
      )?.length
    ) {
      if (
        !documents
          ?.filter(
            (item) => !item.documentCategoryId?.includes('identification'),
          )
          ?.filter((item) => item.status === 'NEW').length
      ) {
        documentsComplete = true;
      }
    } else if (
      !documents?.filter(
        (item) => !item.documentCategoryId?.includes('identification'),
      )?.length
    ) {
      documentsComplete = true;
    }
  }

  const stepGeneralTwoComplete = Boolean(
    stepTwoCompleted &&
    correspondenceAddressComplete &&
    secondPassportComplete &&
    pepComplete &&
    crsAndFatcaComplete &&
    documentsComplete,
  );

  if (stepGeneralTwoComplete) {
    stepTwoStatus = NewApplicationStepStatus.COMPLETED;
  } else if (
    Object.keys(stepTwo).filter((key) => stepTwo[key as keyof FormValues])
      .length
  ) {
    stepTwoStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  return stepTwoStatus;
};

export const getStepThreeStatus = (
  agreements?: ApplicationAgreement[],
  stepsStatus?: NewApplicationStepStatus,
  agreementsActive?: boolean | null,
  agreementsRequired?: boolean | null,
) => {
  let stepThreeStatus = NewApplicationStepStatus.AWAITING || stepsStatus;

  const unsignedAgreements: ApplicationAgreement[] = [];

  agreements?.forEach((item) => {
    if (!item.signatures.edges.length) {
      unsignedAgreements.push(item);
    }
  });

  let agreementsCompleted = false;

  if (agreementsActive && !agreementsRequired) {
    agreementsCompleted = true;
  } else if (
    ((agreementsActive === null || agreementsActive === undefined) &&
      !unsignedAgreements.length) ||
    (agreementsRequired && !unsignedAgreements.length)
  ) {
    agreementsCompleted = true;
  } else if (
    agreementsActive !== null &&
    agreementsActive !== undefined &&
    !agreementsActive
  ) {
    agreementsCompleted = true;
  }

  if (agreementsCompleted) {
    stepThreeStatus = NewApplicationStepStatus.COMPLETED;
  }

  return stepThreeStatus;
};

export const getStepFourStatus = (
  ongoingMonitoring?: OngoingMonitring[],
  ongoingCheckingStatus?: OngoingCheckingStatus,
  stepsStatus?: NewApplicationStepStatus,
) => {
  let stepFourStatus = NewApplicationStepStatus.AWAITING || stepsStatus;

  const ongoingWithWarnings: OngoingMonitring[] = [];

  if (ongoingMonitoring && ongoingMonitoring?.length > 0) {
    stepFourStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  ongoingMonitoring?.forEach((item) => {
    item?.warnings?.edges?.forEach((warning) => {
      if (!warning?.node?.action || warning?.node?.action === 'STOP') {
        ongoingWithWarnings.push(item);
      }
    });
  });

  if (ongoingMonitoring && !ongoingWithWarnings.length) {
    stepFourStatus = NewApplicationStepStatus.COMPLETED;
  } else if (ongoingCheckingStatus?.status === OngoingStatus.FAIL) {
    stepFourStatus = NewApplicationStepStatus.COMPLETED;
  }

  return stepFourStatus;
};

export const updateApplicationStepStatuses = ({
  values,
  stepsStatus,
  agreements,
  ongoingMonitoring,
  ongoingCheckingStatus,
  applicationScheme,
  questionnarieFields,
  crsAndFatcaValues,
  haveSecondPassport,
  documents,
  affilatedCompaniesValues,
  clientsActivities,
}: {
  values: FormValues;
  stepsStatus?: NewApplicationIndividualStepsStatuses;
  agreements?: ApplicationAgreement[];
  ongoingMonitoring?: OngoingMonitring[];
  ongoingCheckingStatus?: OngoingCheckingStatus;
  applicationScheme?: ApplicationScheme;
  haveSecondPassport?: boolean;
  documents?: ApplicationDocument[];
  questionnarieFields?: COFAndIndividualQuestionnarieFormValues;
  crsAndFatcaValues?: CRSAndFatcaFormValues[];
  affilatedCompaniesValues?: AffilatedCompanyFormValues[];
  clientsActivities?: SelectItemProps[];
}): NewApplicationIndividualStepsStatuses => {
  let agreementsActive = null;
  let agreementsRequired = null;

  if (
    applicationScheme &&
    applicationScheme?.agreementsActive?.toString() !== 'null'
  ) {
    agreementsActive = applicationScheme?.agreementsActive;
  }

  if (
    applicationScheme &&
    applicationScheme?.agreementsRequired?.toString() !== 'null'
  ) {
    agreementsRequired = applicationScheme?.agreementsRequired;
  }

  const nextStepsStatus: NewApplicationIndividualStepsStatuses = {
    stepOne: getStepOneStatus(
      values,
      stepsStatus?.stepOne,
      applicationScheme?.taxNumberRequired,
      clientsActivities,
      applicationScheme,
      questionnarieFields,
      affilatedCompaniesValues,
    ),
    stepTwo: getStepTwoStatus(
      values,
      stepsStatus?.stepTwo,
      applicationScheme?.kycProvider || null,
      applicationScheme?.kycRequired || false,
      applicationScheme,
      questionnarieFields,
      crsAndFatcaValues,
      haveSecondPassport,
      documents,
    ),
    stepThree: getStepThreeStatus(
      agreements,
      stepsStatus?.stepThree,
      agreementsActive,
      agreementsRequired,
    ),
    stepFour: getStepFourStatus(
      ongoingMonitoring,
      ongoingCheckingStatus,
      stepsStatus?.stepFour,
    ),
  };

  return nextStepsStatus;
};
